import { MouseEvent, useState } from "react";
import { Mui, MuiIcons } from "@leap4/frontend";
import { useCampaignScope } from "../scope/CampaignScopeProvider";

const { Button, Menu, MenuItem, ListItemText, Typography, Box, Skeleton } = Mui;
const { SwapHoriz, Check } = MuiIcons;

/** Sits where the DSP's AdvertiserSwitcher does; the selected campaign applies to every page. */
export const CampaignScopeSwitcher = () => {
    const { campaigns, campaign, setCampaign, status } = useCampaignScope();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    if (status === "loading") return <Skeleton width={140} height={28} />;
    if (status === "error") {
        return (
            <Typography variant="body2" color="error" role="alert">
                Couldn't load your campaigns
            </Typography>
        );
    }
    if (campaigns.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                No campaigns assigned
            </Typography>
        );
    }

    // One campaign: nothing to switch, so it reads as plain context.
    if (campaigns.length === 1) {
        return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary">
                    Campaign
                </Typography>
                <Typography variant="body2" noWrap sx={{ fontWeight: 600, color: "primary.main" }}>
                    {campaigns[0]}
                </Typography>
            </Box>
        );
    }

    const choose = (next: string) => {
        setCampaign(next);
        setAnchorEl(null);
    };

    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
            <Typography variant="body2" color="text.secondary">
                Campaign
            </Typography>
            <Button
                onClick={(e: MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget)}
                startIcon={<SwapHoriz />}
                aria-haspopup="listbox"
                aria-expanded={open || undefined}
                aria-controls={open ? "campaign-menu" : undefined}
                aria-label={`Campaign: ${campaign}. Change campaign`}
                sx={{ color: "primary.main", fontWeight: 600, minHeight: 40, maxWidth: { xs: 180, sm: 320 } }}
            >
                <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {campaign}
                </Box>
            </Button>
            <Menu
                id="campaign-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
                slotProps={{ paper: { sx: { minWidth: 220, mt: 1 } }, list: { role: "listbox", "aria-label": "Campaign" } }}
            >
                {campaigns.map((option) => {
                    const selected = option === campaign;
                    return (
                        <MenuItem key={option} role="option" aria-selected={selected} selected={selected} onClick={() => choose(option)}>
                            <ListItemText primary={option} />
                            {selected && <Check fontSize="small" sx={{ ml: 2, color: "primary.main" }} />}
                        </MenuItem>
                    );
                })}
            </Menu>
        </Box>
    );
};
